import React, { Component } from 'react';
import { array, arrayOf, bool, func, number, object, shape, string } from 'prop-types';
import pickBy from 'lodash/pickBy';
import classNames from 'classnames';

import appSettings from '../../config/settings';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { FormattedMessage, intlShape, useIntl } from '../../util/reactIntl';
import { isMainSearchTypeKeywords, isOriginInUse } from '../../util/search';
import { withViewport } from '../../util/uiHelpers';
import { parse, stringify } from '../../util/urlHelpers';
import { createResourceLocatorString, pathByRouteName } from '../../util/routes';
import { propTypes } from '../../util/types';
import {
  Button,
  Icons,
  LimitedAccessBanner,
  LinkedLogo,
  ModalMissingInformation,
  NamedLink,
} from '../../components';

import TopbarSearchForm from './TopbarSearchForm/TopbarSearchForm';
import TopbarDesktop from './TopbarDesktop/TopbarDesktop';

import css from './Topbar.module.css';
import SectionTopbarNote from './SectionTopbarNote';
import MobileNavbar from './MobileNavbar/MobileNavbar';
import TabletChipStrip from './TabletChipStrip/TabletChipStrip';
import AccountSheet from '../AccountSheet/AccountSheet';
import { useSelector } from 'react-redux';
import { getListingCategories } from '../../ducks/hostedAssets.duck';

const MAX_MOBILE_SCREEN_WIDTH = 1100;

const redirectToURLWithModalState = (props, modalStateParam) => {
  const { history, location } = props;
  const { pathname, search, state } = location;
  const searchString = `?${stringify({ [modalStateParam]: 'open', ...parse(search) })}`;
  history.push(`${pathname}${searchString}`, state);
};

const redirectToURLWithoutModalState = (props, modalStateParam) => {
  const { history, location } = props;
  const { pathname, search, state } = location;
  const queryParams = pickBy(parse(search), (v, k) => {
    return k !== modalStateParam;
  });
  const stringified = stringify(queryParams);
  const searchString = stringified ? `?${stringified}` : '';
  history.push(`${pathname}${searchString}`, state);
};

const GenericError = props => {
  const { show } = props;
  const classes = classNames(css.genericError, {
    [css.genericErrorVisible]: show,
  });
  return (
    <div className={classes}>
      <div className={css.genericErrorContent}>
        <p className={css.genericErrorText}>
          <FormattedMessage id="Topbar.genericError" />
        </p>
      </div>
    </div>
  );
};

GenericError.propTypes = {
  show: bool.isRequired,
};

class TopbarComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isMobileNavOpen: false,
      isAccountSheetOpen: false,
    };

    this.handleMobileMenuOpen = this.handleMobileMenuOpen.bind(this);
    this.handleMobileMenuClose = this.handleMobileMenuClose.bind(this);
    this.handleMobileSearchOpen = this.handleMobileSearchOpen.bind(this);
    this.handleMobileSearchClose = this.handleMobileSearchClose.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleLogout = this.handleLogout.bind(this);
    this.onOpenMobileNav = this.onOpenMobileNav.bind(this);
    this.handleCloseBrowseMenu = this.handleCloseBrowseMenu.bind(this);
    this.handleTakeoverKeyDown = this.handleTakeoverKeyDown.bind(this);
  }

  onOpenMobileNav(bool) {
    this.setState({ isMobileNavOpen: bool });
  }

  componentDidUpdate(prevProps) {
    // The hamburger-opened browse menu is local state (the Browse tab's
    // ?mobilemenu=open variant closes itself when the param drops). Routes
    // are keyed by name, so same-route navigation (/flute → /whistle) keeps
    // this Topbar instance mounted — close the menu on any location change
    // or it stays open over the new page with the burger stuck in X state.
    const { location } = this.props;
    const prev = prevProps.location;
    if (
      this.state.isMobileNavOpen &&
      (location?.pathname !== prev?.pathname || location?.search !== prev?.search)
    ) {
      this.setState({ isMobileNavOpen: false });
    }
  }

  handleMobileMenuOpen() {
    redirectToURLWithModalState(this.props, 'mobilemenu');
  }

  handleMobileMenuClose() {
    redirectToURLWithoutModalState(this.props, 'mobilemenu');
  }

  handleMobileSearchOpen() {
    redirectToURLWithModalState(this.props, 'mobilesearch');
  }

  handleMobileSearchClose() {
    redirectToURLWithoutModalState(this.props, 'mobilesearch');
  }

  // Closes the browse menu, whichever way it was opened (burger or the Browse tab)
  handleCloseBrowseMenu() {
    this.onOpenMobileNav(false);
    if (parse(this.props.location.search).mobilemenu === 'open') {
      this.handleMobileMenuClose();
    }
  }

  handleTakeoverKeyDown(e) {
    if (e.key === 'Escape') {
      this.handleMobileSearchClose();
    }
  }

  handleSubmit(values) {
    const { currentSearchParams } = this.props;
    const { history, config, routeConfiguration } = this.props;

    const topbarSearchParams = () => {
      if (isMainSearchTypeKeywords(config)) {
        return { keywords: values?.keywords };
      }
      // topbar search defaults to 'location' search
      const { search, selectedPlace } = values?.location;
      const { origin, bounds } = selectedPlace;
      const originMaybe = isOriginInUse(config) ? { origin } : {};

      return {
        ...originMaybe,
        address: search,
        bounds,
      };
    };
    const searchParams = {
      ...currentSearchParams,
      ...topbarSearchParams(),
    };
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, searchParams));
  }

  handleLogout() {
    const { onLogout, history, routeConfiguration } = this.props;
    onLogout().then(() => {
      const path = pathByRouteName('LandingPage', routeConfiguration);

      // In production we ensure that data is really lost,
      // but in development mode we use stored values for debugging
      if (appSettings.dev) {
        history.push(path);
      } else if (typeof window !== 'undefined') {
        window.location = path;
      }

      console.log('logged out'); // eslint-disable-line
    });
  }

  render() {
    const {
      className,
      rootClassName,
      desktopClassName,
      mobileRootClassName,
      mobileClassName,
      isAuthenticated,
      authScopes,
      currentUser,
      currentUserHasListings,
      currentUserHasOrders,
      currentPage,
      notificationCount,
      viewport,
      listingCategories,
      intl,
      location,
      onManageDisableScrolling,
      onOpenBrowseMenu,
      onResendVerificationEmail,
      sendVerificationEmailInProgress,
      sendVerificationEmailError,
      showGenericError,
      config,
      history,
      currentUserCartCount,
    } = this.props;
    // Use the cart count from Redux state (which fetches actual valid listings)
    const updatedCartCount = currentUserCartCount || 0;
    const { mobilemenu, mobilesearch, keywords, address, origin, bounds } = parse(location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });

    const isMobileLayout = viewport.width < MAX_MOBILE_SCREEN_WIDTH;
    const isMobileSearchOpen = isMobileLayout && mobilesearch === 'open';

    const topbarSearcInitialValues = () => {
      if (isMainSearchTypeKeywords(config)) {
        return { keywords };
      }

      // Only render current search if full place object is available in the URL params
      const locationFieldsPresent = isOriginInUse(config)
        ? address && origin && bounds
        : address && bounds;
      return {
        location: locationFieldsPresent
          ? {
              search: address,
              selectedPlace: { address, origin, bounds },
            }
          : null,
      };
    };
    const initialSearchFormValues = topbarSearcInitialValues();

    const classes = classNames(rootClassName || css.root, className);
    const marketplaceName = config.marketplaceName;

    return (
      <div className={classes}>
        <LimitedAccessBanner
          isAuthenticated={isAuthenticated}
          authScopes={authScopes}
          currentUser={currentUser}
          onLogout={this.handleLogout}
          currentPage={currentPage}
        />
        <SectionTopbarNote intl={intl} />
        <div className={classNames(mobileRootClassName || css.container, mobileClassName)}>
          {isMobileSearchOpen ? (
            /* Search takeover (prototype): the topbar row becomes
               magnifier + borderless input + close, same as desktop */
            <div
              className={classNames(css.topbarContentMobile, css.mobileTakeoverRow)}
              onKeyDown={this.handleTakeoverKeyDown}
            >
              <span className={css.mobileTakeoverIcon}>
                <Icons name="bottomSearch" />
              </span>
              <TopbarSearchForm
                className={css.mobileTakeoverForm}
                onSubmit={this.handleSubmit}
                initialValues={initialSearchFormValues}
                appConfig={config}
                autoFocus
                takeover
              />
              <button
                type="button"
                onClick={this.handleMobileSearchClose}
                className={css.mobileTakeoverClose}
                aria-label={intl.formatMessage({ id: 'TopbarDesktop.closeSearch' })}
              >
                <Icons name="closeIcon" />
              </button>
            </div>
          ) : (
            <div className={css.topbarContentMobile}>
              <div className={css.leftSec}>
                <Button
                  rootClassName={css.menu}
                  onClick={() => {
                    const isOpen = this.state.isMobileNavOpen || mobilemenu === 'open';
                    if (isOpen) {
                      this.handleCloseBrowseMenu();
                    } else {
                      this.onOpenMobileNav(true);
                      onOpenBrowseMenu();
                    }
                  }}
                  title={intl.formatMessage({ id: 'Topbar.menuIcon' })}
                >
                  {/* Two-line mark morphs into an X while the menu is open */}
                  <span
                    className={classNames(css.burger, {
                      [css.burgerOpen]: this.state.isMobileNavOpen || mobilemenu === 'open',
                    })}
                  >
                    <span />
                    <span />
                  </span>
                </Button>
              </div>
              <LinkedLogo
                layout={'mobile'}
                alt={intl.formatMessage({ id: 'Topbar.logoIcon' })}
                className={css.mobileLogo}
              />
              <div className={css.rightSec}>
                {/* Tablet icon set (550–1099px, prototype): search · heart · bag · person;
                  phone keeps search and the bag. Inbox lives under Account. */}
                <button
                  type="button"
                  onClick={this.handleMobileSearchOpen}
                  className={classNames(css.headerButton, css.strokeIcon)}
                  aria-label={intl.formatMessage({ id: 'TopbarDesktop.search' })}
                >
                  <Icons name="bottomSearch" />
                </button>
                <NamedLink
                  name="FavoritesPage"
                  className={classNames(css.tabletIcon, css.strokeIcon)}
                  aria-label={intl.formatMessage({ id: 'TopbarDesktop.favorites' }, { count: 0 })}
                >
                  <Icons name="unfavorite" />
                </NamedLink>
                <NamedLink
                  name="CartPage"
                  className={classNames(css.cart, css.strokeIcon)}
                  aria-label={intl.formatMessage(
                    { id: 'TopbarDesktop.cart' },
                    { count: Number(updatedCartCount) || 0 }
                  )}
                >
                  <Icons name="cart" />
                  {updatedCartCount > 0 && (
                    <span className={css.notification} aria-hidden="true">
                      {updatedCartCount}
                    </span>
                  )}
                </NamedLink>
                <button
                  type="button"
                  onClick={() => this.setState({ isAccountSheetOpen: true })}
                  className={classNames(
                    css.tabletIcon,
                    css.strokeIcon,
                    css.accountIconWrap,
                    css.buttonReset
                  )}
                  aria-label={intl.formatMessage({ id: 'TopbarDesktop.accountMenu' })}
                >
                  <Icons name="bottomAccount" />
                  {notificationCount > 0 ? (
                    <span className={css.accountBadge} aria-hidden="true">
                      {notificationCount}
                    </span>
                  ) : null}
                </button>
              </div>
            </div>
          )}
        </div>
        <div className={css.desktop}>
          <TopbarDesktop
            className={desktopClassName}
            currentUserHasListings={currentUserHasListings}
            currentUser={currentUser}
            currentPage={currentPage}
            initialSearchFormValues={initialSearchFormValues}
            intl={intl}
            history={history}
            isAuthenticated={isAuthenticated}
            notificationCount={notificationCount}
            onLogout={this.handleLogout}
            onSearchSubmit={this.handleSubmit}
            appConfig={config}
            updatedCartCount={updatedCartCount}
          />
        </div>
        <TabletChipStrip categories={listingCategories || []} />
        <AccountSheet
          isOpen={this.state.isAccountSheetOpen}
          onClose={() => this.setState({ isAccountSheetOpen: false })}
        />
        {/* Hamburger toggles via local state; the bottom nav's Browse tab
            via the ?mobilemenu=open URL param */}
        {this.state.isMobileNavOpen || mobilemenu === 'open' ? (
          <MobileNavbar onClose={this.handleCloseBrowseMenu} />
        ) : null}
        <ModalMissingInformation
          id="MissingInformationReminder"
          containerClassName={css.missingInformationModal}
          currentUser={currentUser}
          currentUserHasListings={currentUserHasListings}
          currentUserHasOrders={currentUserHasOrders}
          location={location}
          onManageDisableScrolling={onManageDisableScrolling}
          onResendVerificationEmail={onResendVerificationEmail}
          sendVerificationEmailInProgress={sendVerificationEmailInProgress}
          sendVerificationEmailError={sendVerificationEmailError}
        />

        <GenericError show={showGenericError} />
      </div>
    );
  }
}

TopbarComponent.defaultProps = {
  className: null,
  rootClassName: null,
  desktopClassName: null,
  mobileRootClassName: null,
  mobileClassName: null,
  notificationCount: 0,
  currentUserCartCount: 0,
  currentUser: null,
  currentUserHasOrders: null,
  currentPage: null,
  sendVerificationEmailError: null,
  authScopes: [],
};

TopbarComponent.propTypes = {
  className: string,
  rootClassName: string,
  desktopClassName: string,
  mobileRootClassName: string,
  mobileClassName: string,
  isAuthenticated: bool.isRequired,
  authScopes: array,
  authInProgress: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentUserHasListings: bool.isRequired,
  currentUserHasOrders: bool,
  currentPage: string,
  notificationCount: number,
  currentUserCartCount: number,
  onLogout: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  // The browse menu opens: its category counts are asked for (browse.duck)
  onOpenBrowseMenu: func.isRequired,
  onResendVerificationEmail: func.isRequired,
  sendVerificationEmailInProgress: bool.isRequired,
  sendVerificationEmailError: propTypes.error,
  showGenericError: bool.isRequired,

  // These are passed from Page to keep Topbar rendering aware of location changes
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string.isRequired,
  }).isRequired,

  // from withViewport
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,
};

const EnhancedTopbar = props => {
  const config = useConfiguration();
  const listingCategories = useSelector(getListingCategories);
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  return (
    <TopbarComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      listingCategories={listingCategories}
      {...props}
    />
  );
};

const Topbar = withViewport(EnhancedTopbar);
Topbar.displayName = 'Topbar';

export default Topbar;
